import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { createApi } from '@Pages/pageRuntime/sdk/api';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkSubscriptionChangePreviewHandle,
  SdkSubscriptionChangePreviewOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export function useSubscriptionChangePreview({
  resourceId,
  recordId,
  items,
  effective = 'now',
  prorationBehavior = 'prorate',
  periodStart,
  revisionScope,
  enabled = true
}: SdkSubscriptionChangePreviewOptions): SdkSubscriptionChangePreviewHandle {
  const canPreview =
    enabled &&
    resourceId.length > 0 &&
    recordId.length > 0 &&
    items.length > 0 &&
    (effective !== 'period' || (periodStart ?? '') !== '');
  // The endpoint rejects an item that carries any other key.
  const selection = items.map(({ price_record_id, quantity, pricing }) => ({
    price_record_id,
    quantity,
    ...(pricing ? { pricing } : {})
  }));
  const body = {
    items: selection,
    effective,
    proration_behavior: prorationBehavior,
    ...(effective === 'period' ? { period_start: periodStart } : {}),
    ...(revisionScope ? { revision_scope: revisionScope } : {})
  };
  const query = useQuery<
    Diagonal.Core.Data.SubscriptionChangePreviewData,
    ReturnType<typeof toApiError>
  >({
    queryKey: [
      PAGE_API_QUERY_KEY_HEAD,
      'records',
      resourceId,
      recordId,
      'subscription-change-preview',
      body
    ],
    enabled: canPreview,
    retry: false,
    refetchOnMount: 'always',
    staleTime: 0,
    throwOnError: false,
    queryFn: async ({ signal }) => {
      return createApi(axios).post<Diagonal.Core.Data.SubscriptionChangePreviewData>(
        `/api/data/resources/${encodeURIComponent(resourceId)}/records/${encodeURIComponent(recordId)}/subscription-change-preview`,
        body,
        { signal }
      );
    }
  });
  const isLoading = canPreview && (query.isFetching || query.isPaused);
  const preview = canPreview && !isLoading && !query.isError ? query.data : undefined;

  return {
    preview,
    quote: preview?.quote ?? null,
    isLoading,
    error: canPreview ? query.error : null,
    refetch: async () => {
      if (canPreview) {
        await query.refetch();
      }
    }
  };
}
